import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import DashboardLayout from "../../../Layout/DashboardLayout";
import { companyID } from "../../../Common/UIComponents";
import {
  fetchTempHumidityConfigList,
  fetchTempHumidityConfigByCompany,
  saveTempHumidityConfig,
} from "../../../API_Service/Settings_Slice";

// Config for how often the Temperature & Humidity capture popup shows in
// Lab Utilization. The option list (code + display name) comes from
// Settings/Get_TempHumidityConfigList (TempHumidityConfig_Mst) — icon/
// description aren't stored in that table, so they're still looked up
// locally by code. Save is a real upsert now: Settings/Save_TempHumidityConfig
// stores the selected option's FrequencyID on Settings.TempHumidityConfigID
// for this company (UPDATE if a Settings row already exists, else INSERT).

const META_BY_CODE = {
  SHIFT: {
    icon: "swap_horiz",
    description: "Ask once whenever a new shift begins.",
  },
  DAY: {
    icon: "today",
    description: "Ask once a day, no matter how many shifts run.",
  },
  "4HRS": {
    icon: "schedule",
    description: "Ask every 4 hours while testing is active.",
  },
  NONE: {
    icon: "notifications_off",
    description: "Don't ask — readings won't be logged this way.",
  },
};

const OptionCard = ({ option, selected, onSelect }) => {
  const isActive = selected === option.code;
  return (
    <button
      type="button"
      onClick={() => onSelect(option.code)}
      className={`relative text-left p-5 rounded-2xl border transition-all
        ${
          isActive
            ? "border-[#08757c] bg-[#08757c]/[0.06] shadow-[0_0_0_1px_#08757c]"
            : "border-[#e5e9e9] bg-white hover:border-[#08757c]/40 hover:bg-[#f7fafa]"
        }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0
            ${isActive ? "bg-[#08757c] text-white" : "bg-[#f1f4f4] text-[#6e797a]"}`}
        >
          <span className="material-symbols-outlined text-[20px]">
            {option.icon}
          </span>
        </div>
        <span
          className={`h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
            ${isActive ? "border-[#08757c]" : "border-[#cdd6d7]"}`}
        >
          {isActive && (
            <span className="h-2.5 w-2.5 rounded-full bg-[#08757c]" />
          )}
        </span>
      </div>
      <p
        className={`text-sm font-bold mb-1 ${isActive ? "text-[#08757c]" : "text-[#181c1d]"}`}
      >
        {option.label}
      </p>
      <p className="text-xs text-[#6e797a] leading-relaxed">
        {option.description}
      </p>
    </button>
  );
};

export default function TemperatureHumidityConfigIndex({
  embedded = false,
} = {}) {
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [frequency, setFrequency] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(false);
    Promise.all([
      dispatch(fetchTempHumidityConfigList()),
      dispatch(fetchTempHumidityConfigByCompany({ companyId: companyID })),
    ])
      .then(([listRes, currentRes]) => {
        const rows = listRes?.payload?.data;
        if (
          !listRes?.payload?.success ||
          !Array.isArray(rows) ||
          rows.length === 0
        ) {
          setLoadError(true);
          return;
        }

        const mapped = rows.map((r) => ({
          id: r.frequencyID,
          code: r.frequencyCode,
          label: r.frequencyName,
        }));
        setOptions(mapped);

        // Whatever's actually saved in Settings.TempHumidityConfigID for
        // this company wins — only fall back to the first option if
        // nothing's been saved yet (currentRes.data is null in that case).
        const saved = currentRes?.payload?.data;
        const savedCode = saved?.frequencyCode;
        setFrequency(
          savedCode && mapped.some((o) => o.code === savedCode)
            ? savedCode
            : mapped[0].code,
        );
      })
      .catch(() => setLoadError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  const handleSave = () => {
    const selectedOption = options.find((o) => o.code === frequency);
    if (!selectedOption) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    dispatch(
      saveTempHumidityConfig({
        companyId: companyID,
        tempHumidityConfigID: selectedOption.id,
      }),
    )
      .then((res) => {
        if (res?.payload?.success) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 4000);
        } else {
          setSaveError("Failed to save. Please try again.");
        }
      })
      .catch(() => setSaveError("Failed to save. Please try again."))
      .finally(() => setIsSaving(false));
  };

  const content = (
    <div
      className={
        embedded
          ? "px-8 py-4 text-[#181c1d]"
          : "px-8 py-4 bg-[#f7fafa] min-h-screen text-[#181c1d]"
      }
    >
      {!embedded && (
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-1.5">
          <span>Administration</span>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span>Application Configuration</span>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span className="text-[#08757C] font-semibold">
            Temperature &amp; Humidity
          </span>
        </nav>
      )}

      {!embedded && (
        <div className="mb-3">
          <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d]">
            Temperature &amp; Humidity
          </h1>
          <p className="text-[#3e494a] text-sm mt-1">
            Control how often the temperature/humidity capture popup shows
            in Lab Utilization.
          </p>
        </div>
      )}

      <div className="bg-white p-5 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
        <h3 className="text-base font-bold flex items-center gap-2 mb-1">
          <span className="material-symbols-outlined text-[#08757c] text-[20px]">
            device_thermostat
          </span>
          Popup Frequency
        </h3>
        <p className="text-xs text-[#6e797a] mb-5">
          Choose exactly one — this decides when the operator gets asked to
          log a new temperature/humidity reading.
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-[#6e797a] text-sm gap-3">
            <span className="material-symbols-outlined animate-spin text-[#08757c]">
              progress_activity
            </span>
            Loading options…
          </div>
        ) : loadError || options.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
            <span className="material-symbols-outlined text-[28px] text-[#ba1a1a]">
              error
            </span>
            <p className="text-sm font-semibold text-[#181c1d]">
              Couldn't load frequency options.
            </p>
            <p className="text-xs text-[#6e797a]">
              Get_TempHumidityConfigList returned nothing — check
              TempHumidityConfig_Mst has active rows.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {options.map((opt) => (
              <OptionCard
                key={opt.code}
                option={{
                  ...opt,
                  icon: META_BY_CODE[opt.code]?.icon ?? "settings",
                  description: META_BY_CODE[opt.code]?.description ?? "",
                }}
                selected={frequency}
                onSelect={setFrequency}
              />
            ))}
          </div>
        )}

        {saveSuccess && (
          <div className="mt-5 bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
            <span className="material-symbols-outlined text-[20px]">
              check_circle
            </span>
            <span className="font-semibold">
              Temperature &amp; Humidity settings saved successfully.
            </span>
          </div>
        )}

        {saveError && (
          <div className="mt-5 bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">
              error
            </span>
            {saveError}
          </div>
        )}

        <div className="flex justify-end mt-5">
          <button
            onClick={handleSave}
            disabled={isSaving || !frequency}
            className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-[18px] animate-spin">
                  progress_activity
                </span>
                Saving…
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">
                  save
                </span>
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
}
